import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  EyeOutlined,
  FolderOpenOutlined,
} from '@ant-design/icons';
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import { ShowcaseProjectStatus, listShowcaseProjects } from '../../api/showcaseProjects';
import type { ShowcaseProjectFilter } from '../../api/showcaseProjects';
import { StatTiles } from '../../layout/StatTiles';
import type { StatTile } from '../../layout/StatTiles';
import { statusColors } from './showcaseDisplay';
import { BrandColors } from '../../theme/brand';

/** Bốn con số đầu trang, giống dải số liệu trên trang Dự án tiêu biểu của website. */
interface SummaryCounts {
  readonly total: number;
  readonly completed: number;
  readonly ongoing: number;
  readonly published: number;
}

/**
 * Đếm số dự án khớp một điều kiện.
 *
 * Chỉ xin một dòng rồi lấy con số tổng máy chủ trả kèm — đúng cả khi danh sách dài hơn một trang,
 * điều mà đếm trên các dòng đang hiện không làm được.
 */
async function countProjects(filter: ShowcaseProjectFilter): Promise<number> {
  const page = await listShowcaseProjects({ ...filter, pageNumber: 1, pageSize: 1 });

  return page.totalCount;
}

/**
 * Dải số liệu đầu trang "Demo dự án".
 *
 * Không chịu ảnh hưởng của bộ lọc bên dưới: nó trả lời "website đang có gì", không phải "bộ lọc
 * đang chọn ra gì".
 */
export function ShowcaseSummary({ reloadToken }: { readonly reloadToken: number }): ReactNode {
  const [counts, setCounts] = useState<SummaryCounts | null>(null);

  useEffect(() => {
    let isCurrent = true;

    async function load(): Promise<void> {
      try {
        const [total, completed, ongoing, published] = await Promise.all([
          countProjects({}),
          countProjects({ status: ShowcaseProjectStatus.Completed }),
          countProjects({ status: ShowcaseProjectStatus.Ongoing }),
          countProjects({ isPublished: true }),
        ]);

        if (isCurrent) {
          setCounts({ total, completed, ongoing, published });
        }
      } catch {
        // Dải số liệu chỉ để tham khảo; danh sách bên dưới đã tự báo lỗi tải.
        if (isCurrent) {
          setCounts(null);
        }
      }
    }

    void load();

    return () => {
      isCurrent = false;
    };
  }, [reloadToken]);

  const items: StatTile[] = [
    {
      key: 'total',
      label: 'Tổng dự án',
      icon: <FolderOpenOutlined />,
      color: BrandColors.ink,
      value: counts?.total,
    },
    {
      key: 'completed',
      label: 'Hoàn thành',
      icon: <CheckCircleOutlined />,
      color: statusColors[ShowcaseProjectStatus.Completed],
      value: counts?.completed,
    },
    {
      key: 'ongoing',
      label: 'Đang thực hiện',
      icon: <ClockCircleOutlined />,
      color: statusColors[ShowcaseProjectStatus.Ongoing],
      value: counts?.ongoing,
    },
    {
      key: 'published',
      label: 'Đang hiện trên web',
      icon: <EyeOutlined />,
      color: BrandColors.gold,
      value: counts?.published,
    },
  ];

  return <StatTiles items={items} />;
}
